import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminAPI, getUser, logout } from '../api';
import '../App.css';

const roleConfig = {
  admin: { label: 'Super Admin', color: '#F0B429', bg: 'rgba(240,180,41,.15)' },
  sav: { label: 'SAV', color: '#F5620A', bg: 'rgba(245,98,10,.15)' },
  commercial: { label: 'Commercial', color: '#22C55E', bg: 'rgba(34,197,94,.12)' },
  livreur: { label: 'Livraison', color: '#1AA3E8', bg: 'rgba(26,163,232,.12)' },
};

const statutCmdConfig = {
  confirme:   { label: 'Confirme', bg: 'rgba(245,98,10,.12)', color: '#F5620A' },
  accepte:    { label: 'Accepte', bg: 'rgba(245,158,11,.12)', color: '#F59E0B' },
  collecte:   { label: 'Collecte', bg: 'rgba(245,158,11,.12)', color: '#F59E0B' },
  transit:    { label: 'En livraison', bg: 'rgba(99,102,241,.12)', color: '#818CF8' },
  livre:      { label: '✓ Livre', bg: 'rgba(34,197,94,.12)', color: '#22C55E' },
  annule:     { label: 'Annule', bg: 'rgba(239,68,68,.12)', color: '#EF4444' },
};

function AdminPage() {
  const navigate = useNavigate();
  const user = getUser();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [commandes, setCommandes] = useState([]);
  const [vendeurs, setVendeurs] = useState([]);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    if (!user || !['admin','sav','commercial','livreur'].includes(user.role)) {
      navigate('/connexion');
      return;
    }
    fetchData();
  }, [activeSection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeSection === 'dashboard' && user.role === 'admin') {
        const res = await adminAPI.stats();
        setStats(res.data);
      }
      if (activeSection === 'commandes') {
        const res = await adminAPI.commandes();
        setCommandes(res.data);
      }
      if (activeSection === 'vendeurs' && ['admin','commercial'].includes(user.role)) {
        const res = await adminAPI.vendeurs();
        setVendeurs(res.data);
      }
      if (activeSection === 'utilisateurs' && user.role === 'admin') {
        const res = await adminAPI.utilisateurs();
        setUtilisateurs(res.data);
      }
      if (activeSection === 'annonces' && ['admin','commercial'].includes(user.role)) {
        const res = await adminAPI.annonces();
        setAnnonces(res.data);
      }
    } catch (err) {
      console.error('Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg, type = '') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const updateVendeurStatut = async (id, statut) => {
    try {
      await adminAPI.updateVendeurStatut(id, statut);
      showToast('Statut mis a jour ✓', 'ok');
      fetchData();
    } catch (err) {
      showToast('Erreur mise a jour', 'err');
    }
  };

  const updateRole = async (id, role) => {
    try {
      await adminAPI.updateRole(id, role);
      showToast('Role mis a jour ✓', 'ok');
      fetchData();
    } catch (err) {
      showToast('Erreur mise a jour', 'err');
    }
  };

  const updateCommandeStatut = async (id, statut) => {
    try {
      await adminAPI.updateCommandeStatut(id, statut);
      showToast('Commande mise a jour ✓', 'ok');
      fetchData();
    } catch (err) {
      showToast('Erreur mise a jour', 'err');
    }
  };

  const updateAnnonceStatut = async (id, statut) => {
    try {
      await adminAPI.updateAnnonceStatut(id, statut);
      showToast('Annonce mise a jour ✓', 'ok');
      fetchData();
    } catch (err) {
      showToast('Erreur mise a jour', 'err');
    }
  };

  const rc = roleConfig[user?.role] || roleConfig.admin;
  const initials = user ? (user.prenom?.[0] || '') + (user.nom?.[0] || '') : 'A';

  const navItems = [
    { key: 'dashboard', icon: '📊', label: 'Vue d\'ensemble', roles: ['admin'] },
    { key: 'commandes', icon: '📦', label: 'Commandes', roles: ['admin','sav','livreur'] },
    { key: 'vendeurs', icon: '🏪', label: 'Vendeurs', roles: ['admin','commercial'] },
    { key: 'annonces', icon: '📋', label: 'Annonces', roles: ['admin','commercial'] },
    { key: 'utilisateurs', icon: '👥', label: 'Utilisateurs', roles: ['admin'] },
  ].filter(item => item.roles.includes(user?.role));

  return (
    <div className="app" style={{ display: 'grid', gridTemplateColumns: '230px 1fr', minHeight: '100vh' }}>

      {/* SIDEBAR */}
      <div style={{ background: '#141414', borderRight: '1px solid #2A2A2A', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #2A2A2A' }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '22px', letterSpacing: '2px', color: '#fff', lineHeight: 1 }}>Tek<span style={{ color: '#F5620A' }}>Teck</span></div>
          </Link>
          <div style={{ fontSize: '9px', fontWeight: '700', color: '#888', letterSpacing: '.8px', marginTop: '2px' }}>ADMINISTRATION</div>
        </div>

        <div style={{ padding: '14px 18px', borderBottom: '1px solid #2A2A2A', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: rc.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '13px', color: '#fff', flexShrink: 0 }}>{initials}</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#fff' }}>{user?.prenom} {user?.nom}</div>
            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '800', background: rc.bg, color: rc.color }}>{rc.label}</span>
          </div>
        </div>

        <div style={{ padding: '10px 12px', flex: 1 }}>
          {navItems.map((item) => (
            <div key={item.key} onClick={() => setActiveSection(item.key)}
              style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '2px', background: activeSection === item.key ? 'rgba(245,98,10,.12)' : 'transparent', border: activeSection === item.key ? '1px solid rgba(245,98,10,.2)' : '1px solid transparent' }}>
              <span style={{ fontSize: '14px' }}>{item.icon}</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: activeSection === item.key ? '#F5620A' : '#888' }}>{item.label}</span>
            </div>
          ))}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #2A2A2A' }}>
          <button onClick={logout} style={{ width: '100%', padding: '8px', background: 'transparent', border: '1.5px solid #333', borderRadius: '8px', fontSize: '12px', fontWeight: '700', color: '#888', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Deconnexion
          </button>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ overflow: 'auto' }}>
        <div style={{ background: '#141414', borderBottom: '1px solid #2A2A2A', padding: '0 28px', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '18px', letterSpacing: '1px', color: '#fff' }}>
              {navItems.find(n => n.key === activeSection)?.label?.toUpperCase() || 'ADMINISTRATION'}
            </div>
            <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', background: rc.bg, color: rc.color }}>{rc.label}</span>
          </div>
        </div>

        <div style={{ padding: '22px 28px 60px' }}>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '32px', marginBottom: '16px' }}>⏳</div>
              <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '20px', color: '#fff' }}>CHARGEMENT...</div>
            </div>
          )}

          {/* DASHBOARD */}
          {!loading && activeSection === 'dashboard' && stats && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '22px' }}>
                {[
                  { label: 'COMMANDES AUJOURD\'HUI', val: stats.commandes_today, sub: `${stats.commandes_total} total`, subColor: '#888' },
                  { label: 'REVENUS PLATEFORME', val: parseInt(stats.revenus_plateforme).toLocaleString('fr-FR'), sub: 'FCFA total', subColor: '#22C55E' },
                  { label: 'LIVRAISONS EN COURS', val: stats.livraisons_en_cours, sub: 'actives', subColor: '#818CF8' },
                  { label: 'VENDEURS ACTIFS', val: stats.vendeurs_actifs, sub: `${stats.acheteurs} acheteurs`, subColor: '#888' },
                ].map((k, i) => (
                  <div key={i} style={{ background: '#1C1C1C', border: '1px solid #333', borderRadius: '12px', padding: '16px 18px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '.4px', marginBottom: '5px' }}>{k.label}</div>
                    <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '24px', letterSpacing: '1px', color: '#fff', marginBottom: '2px' }}>{k.val}</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: k.subColor }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#1C1C1C', border: '1px solid #333', borderRadius: '14px', padding: '18px 22px' }}>
                <div style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '16px', letterSpacing: '1px', color: '#fff', marginBottom: '14px' }}>GESTION DES ROLES</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px' }}>
                  {[
                    { role: 'admin', label: 'Super Admin', desc: 'Acces complet', icon: '👑', color: '#F0B429' },
                    { role: 'sav', label: 'SAV', desc: 'Reclamations & litiges', icon: '🛡️', color: '#F5620A' },
                    { role: 'commercial', label: 'Commercial', desc: 'Vendeurs & annonces', icon: '💼', color: '#22C55E' },
                    { role: 'livreur', label: 'Livraison', desc: 'Suivi livraisons', icon: '🛵', color: '#1AA3E8' },
                  ].map(r => (
                    <div key={r.role} style={{ background: '#141414', border: `1px solid ${r.color}30`, borderRadius: '10px', padding: '14px' }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{r.icon}</div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#fff', marginBottom: '4px' }}>{r.label}</div>
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>{r.desc}</div>
                      <button onClick={() => setActiveSection('utilisateurs')} style={{ width: '100%', padding: '7px', background: `${r.color}20`, border: `1px solid ${r.color}40`, borderRadius: '7px', fontSize: '11px', fontWeight: '700', color: r.color, cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        Gerer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* COMMANDES */}
          {!loading && activeSection === 'commandes' && (
            <div>
              {commandes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Aucune commande</div>
              ) : (
                <div style={{ background: '#1C1C1C', border: '1px solid #333', borderRadius: '14px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Ref','Piece','Acheteur','Vendeur','Statut','Total','Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '12px 14px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '.4px', borderBottom: '1px solid #2A2A2A', background: '#141414' }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {commandes.map((cmd, i) => {
                        const sc = statutCmdConfig[cmd.statut] || statutCmdConfig.confirme;
                        return (
                          <tr key={cmd.id} style={{ borderBottom: i < commandes.length - 1 ? '1px solid #2A2A2A' : 'none' }}>
                            <td style={{ padding: '10px 14px', fontSize: '11px', fontWeight: '700', color: '#fff' }}>{cmd.reference}</td>
                            <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{cmd.nom_piece}</td>
                            <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{cmd.acheteur_prenom} {cmd.acheteur_nom}</td>
                            <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{cmd.vendeur_prenom} {cmd.vendeur_nom}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', background: sc.bg, color: sc.color }}>{sc.label}</span>
                            </td>
                            <td style={{ padding: '10px 14px', fontFamily: 'Bebas Neue, sans-serif', fontSize: '14px', color: '#F5620A' }}>{parseInt(cmd.total).toLocaleString('fr-FR')} F</td>
                            <td style={{ padding: '10px 14px' }}>
                              <select onChange={e => e.target.value && updateCommandeStatut(cmd.id, e.target.value)} defaultValue=""
                                style={{ padding: '4px 8px', background: '#141414', border: '1.5px solid #333', borderRadius: '6px', fontSize: '11px', color: '#fff', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                <option value="">Changer...</option>
                                {['confirme','accepte','collecte','transit','livre','annule'].map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* VENDEURS */}
          {!loading && activeSection === 'vendeurs' && (
            <div>
              {vendeurs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Aucun vendeur</div>
              ) : (
                <div style={{ background: '#1C1C1C', border: '1px solid #333', borderRadius: '14px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Vendeur','Ville','Annonces','Ventes','Statut','Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '12px 14px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '.4px', borderBottom: '1px solid #2A2A2A', background: '#141414' }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {vendeurs.map((v, i) => (
                        <tr key={v.id} style={{ borderBottom: i < vendeurs.length - 1 ? '1px solid #2A2A2A' : 'none' }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>{v.prenom} {v.nom}</div>
                            <div style={{ fontSize: '10px', color: '#888' }}>{v.phone}</div>
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{v.ville}</td>
                          <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{v.nb_annonces}</td>
                          <td style={{ padding: '10px 14px', fontSize: '11px', color: '#F5620A', fontWeight: '700' }}>{parseInt(v.revenus_total).toLocaleString('fr-FR')} F</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', background: v.statut === 'actif' ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)', color: v.statut === 'actif' ? '#22C55E' : '#EF4444' }}>
                              {v.statut}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {v.statut === 'actif' ? (
                                <button onClick={() => updateVendeurStatut(v.id, 'suspendu')} style={{ padding: '4px 10px', border: '1.5px solid #EF4444', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', background: 'transparent', color: '#EF4444', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Suspendre</button>
                              ) : (
                                <button onClick={() => updateVendeurStatut(v.id, 'actif')} style={{ padding: '4px 10px', border: '1.5px solid #22C55E', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', background: 'transparent', color: '#22C55E', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Reactiver</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ANNONCES */}
          {!loading && activeSection === 'annonces' && (
            <div>
              {annonces.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Aucune annonce</div>
              ) : (
                <div style={{ background: '#1C1C1C', border: '1px solid #333', borderRadius: '14px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Piece','Vendeur','Prix','Statut','Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '12px 14px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '.4px', borderBottom: '1px solid #2A2A2A', background: '#141414' }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {annonces.map((a, i) => (
                        <tr key={a.id} style={{ borderBottom: i < annonces.length - 1 ? '1px solid #2A2A2A' : 'none' }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>{a.nom_piece}</div>
                            <div style={{ fontSize: '10px', color: '#888' }}>{a.type_vehicule} · {a.categorie}</div>
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{a.prenom} {a.nom}</td>
                          <td style={{ padding: '10px 14px', fontFamily: 'Bebas Neue, sans-serif', fontSize: '14px', color: '#F5620A' }}>{parseInt(a.prix).toLocaleString('fr-FR')} F</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', background: a.statut === 'active' ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)', color: a.statut === 'active' ? '#22C55E' : '#EF4444' }}>
                              {a.statut}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {a.statut === 'active' ? (
                                <button onClick={() => updateAnnonceStatut(a.id, 'suspendue')} style={{ padding: '4px 10px', border: '1.5px solid #EF4444', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', background: 'transparent', color: '#EF4444', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Suspendre</button>
                              ) : (
                                <button onClick={() => updateAnnonceStatut(a.id, 'active')} style={{ padding: '4px 10px', border: '1.5px solid #22C55E', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', background: 'transparent', color: '#22C55E', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Reactiver</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* UTILISATEURS */}
          {!loading && activeSection === 'utilisateurs' && (
            <div>
              {utilisateurs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Aucun utilisateur</div>
              ) : (
                <div style={{ background: '#1C1C1C', border: '1px solid #333', borderRadius: '14px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Utilisateur','Ville','Role','Statut','Changer role'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '12px 14px', fontSize: '10px', fontWeight: '700', color: '#888', letterSpacing: '.4px', borderBottom: '1px solid #2A2A2A', background: '#141414' }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {utilisateurs.map((u, i) => {
                        const rc2 = roleConfig[u.role] || { label: u.role, color: '#888', bg: 'rgba(255,255,255,.06)' };
                        return (
                          <tr key={u.id} style={{ borderBottom: i < utilisateurs.length - 1 ? '1px solid #2A2A2A' : 'none' }}>
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>{u.prenom} {u.nom}</div>
                              <div style={{ fontSize: '10px', color: '#888' }}>{u.phone}</div>
                            </td>
                            <td style={{ padding: '10px 14px', fontSize: '11px', color: '#888' }}>{u.ville}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', background: rc2.bg, color: rc2.color }}>{rc2.label || u.role}</span>
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '800', background: u.statut === 'actif' ? 'rgba(34,197,94,.12)' : 'rgba(239,68,68,.12)', color: u.statut === 'actif' ? '#22C55E' : '#EF4444' }}>
                                {u.statut}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <select onChange={e => e.target.value && updateRole(u.id, e.target.value)} defaultValue={u.role}
                                style={{ padding: '4px 8px', background: '#141414', border: '1.5px solid #333', borderRadius: '6px', fontSize: '11px', color: '#fff', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                {['acheteur','vendeur','admin','sav','commercial','livreur'].map(r => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TOASTS */}
      <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', zIndex: 999, display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
        {toasts.map(t => (
          <div key={t.id} style={{ background: t.type === 'ok' ? '#22C55E' : t.type === 'err' ? '#EF4444' : '#1C1C1C', color: '#fff', padding: '11px 22px', borderRadius: '9px', fontSize: '13px', fontWeight: '700', boxShadow: '0 4px 20px rgba(0,0,0,.4)', whiteSpace: 'nowrap' }}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminPage;